/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, X, Phone, Activity } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import AIBadge from './AIBadge';
import { createPortal } from 'react-dom';
import api from '../../lib/api';
import AnimatedCounter from '../ui/AnimatedCounter';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * AIBriefingHeader — Global daily briefing pill, mounted in HeaderBar.
 *
 * - Fetches /cockpit-ai/briefing on mount + every 30 min
 * - Renders a compact glass pill "Bonjour Sophie. 3 priorités · 18 400 €"
 * - On click → opens a side-sheet listing the 3 top actions with deep links
 * - Hidden if priorities_count === 0
 */
export default function AIBriefingHeader({ compact = false }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isDark } = useTheme();
    // Le briefing IA est une fonctionnalité Cockpit réservée aux agents/managers.
    // Inutile (et 403) pour les vendeurs → on n'appelle pas l'endpoint.
    const isCockpitUser = user?.role === 'admin' || user?.role === 'agent';

    const fetchBriefing = useCallback(async () => {
        try {
            const res = await api.get('/cockpit-ai/briefing');
            setData(res.data);
        } catch {
            // silent — header doit toujours fonctionner même sans briefing
            setData(null);
        }
    }, []);

    useEffect(() => {
        if (!isCockpitUser) return;
        fetchBriefing();
        const interval = setInterval(fetchBriefing, 30 * 60 * 1000); // 30 min
        return () => clearInterval(interval);
    }, [fetchBriefing, isCockpitUser]);

    // Don't render if no priorities OR data not loaded yet OR utilisateur non-cockpit
    if (!isCockpitUser || !data || data.priorities_count === 0) return null;

    const handleAction = (link) => {
        if (link) navigate(link);
        setOpen(false);
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`group relative inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full border transition-all overflow-hidden ${
                    isDark ? 'border-foreground/10 hover:border-foreground/20' : 'border-[#681793]/20 hover:border-[#681793]/40'
                }`}
                style={{
                    background: isDark
                        ? 'linear-gradient(135deg, rgba(104,23,147,0.18) 0%, rgba(255,145,77,0.10) 100%)'
                        : 'linear-gradient(135deg, rgba(104,23,147,0.08) 0%, rgba(255,145,77,0.06) 100%)',
                    fontFamily: 'Space Grotesk, sans-serif',
                }}
                data-testid="ai-briefing-pill"
                aria-label={`${data.priorities_count} priorités, ${data.exposed_amount} euros de pipeline exposé`}
            >
                <span className="relative w-6 h-6 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                    <Sparkles className="w-3 h-3 text-white" />
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#FF914D] animate-pulse" />
                </span>

                <span className={`text-xs leading-none ${isDark ? 'text-white' : 'text-[#1a0f2e]'}`}>
                    <span className={isDark ? 'text-foreground/60' : 'text-[#1a0f2e]/60'}>
                        {data.greeting} {data.manager_first_name && data.manager_first_name + ' · '}
                    </span>
                    <span
                        className={`font-semibold ${isDark ? 'text-[#FF914D]' : 'text-[#E66A1A]'}`}
                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                    >
                        {data.priorities_count}
                    </span>{' '}
                    <span className={`hidden sm:inline ${isDark ? 'text-foreground/80' : 'text-[#1a0f2e]/80'}`}>
                        priorité{data.priorities_count > 1 ? 's' : ''}
                    </span>
                    {!compact && data.exposed_amount > 0 && (
                        <>
                            <span className={`mx-2 ${isDark ? 'text-foreground/30' : 'text-[#1a0f2e]/30'}`}>·</span>
                            <span
                                className={`font-semibold ${isDark ? 'text-white' : 'text-[#1a0f2e]'}`}
                                style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                            >
                                <AnimatedCounter value={data.exposed_amount} suffix=" €" />
                            </span>
                        </>
                    )}
                </span>

                <ArrowRight className={`w-3 h-3 transition-colors hidden md:inline ${
                    isDark ? 'text-foreground/40 group-hover:text-foreground/80' : 'text-[#1a0f2e]/40 group-hover:text-[#1a0f2e]/80'
                }`} />
            </button>

            {/* Side-sheet */}
            <AnimatePresence>
                {open && (
                    <BriefingSheet data={data} onClose={() => setOpen(false)} onAction={handleAction} />
                )}
            </AnimatePresence>
        </>
    );
}

function BriefingSheet({ data, onClose, onAction }) {
    return createPortal(
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1900]"
                data-testid="ai-briefing-sheet-backdrop"
            />
            <motion.aside
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 280 }}
                className="fixed right-0 top-0 bottom-0 z-[2000] w-full max-w-md bg-[#0A0A0A] border-l border-foreground/10 flex flex-col overflow-hidden"
                style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
                data-testid="ai-briefing-sheet"
                role="dialog"
                aria-label="Briefing IA détaillé"
            >
                {/* Header */}
                <div className="relative p-5 border-b border-foreground/10">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#681793]/20 blur-3xl pointer-events-none" />
                    <div className="relative flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p
                                        className="text-[10px] uppercase tracking-widest text-foreground/40"
                                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                    >
                                        Briefing IA · Aujourd'hui
                                    </p>
                                    <AIBadge persona="pilote" size="sm" />
                                </div>
                                <h3
                                    className="text-lg font-semibold text-white leading-tight"
                                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                >
                                    {data.greeting} {data.manager_first_name}
                                </h3>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-foreground/10 text-foreground/60 hover:text-foreground transition-colors"
                            data-testid="ai-briefing-sheet-close"
                            aria-label="Fermer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Key numbers */}
                <div className="grid grid-cols-2 gap-3 p-5 border-b border-foreground/10">
                    <div className="p-3 rounded-xl bg-foreground/[0.03] border border-foreground/10">
                        <p
                            className="text-[10px] uppercase tracking-widest text-foreground/40 mb-1"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            Priorités
                        </p>
                        <span
                            className="text-3xl font-medium text-[#FF914D]"
                            style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        >
                            <AnimatedCounter value={data.priorities_count} />
                        </span>
                    </div>
                    <div className="p-3 rounded-xl bg-foreground/[0.03] border border-foreground/10">
                        <p
                            className="text-[10px] uppercase tracking-widest text-foreground/40 mb-1"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            Exposé
                        </p>
                        <span
                            className="text-2xl font-medium text-white"
                            style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                        >
                            <AnimatedCounter value={data.exposed_amount} suffix=" €" />
                        </span>
                    </div>
                </div>

                {/* Actions list */}
                <div className="flex-1 overflow-y-auto p-5 space-y-2">
                    <p
                        className="text-[10px] uppercase tracking-widest text-foreground/40 mb-3"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                        Top {data.actions?.length || 0} action{(data.actions?.length || 0) > 1 ? 's' : ''} prioritaire{(data.actions?.length || 0) > 1 ? 's' : ''}
                    </p>
                    {(!data.actions || data.actions.length === 0) ? (
                        <p className="text-sm text-foreground/50 italic">Aucune action prioritaire détectée — beau travail.</p>
                    ) : (
                        data.actions.map((action, i) => {
                            const IconComp = LucideIcons[action.icon] || Phone;
                            return (
                                <motion.button
                                    key={`briefing-action-${i}-${action.label}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.08 }}
                                    onClick={() => onAction(action.link)}
                                    className="w-full group flex items-start gap-3 p-3 rounded-xl bg-foreground/[0.03] border border-foreground/10 hover:border-[#FF914D]/30 hover:bg-foreground/[0.05] text-left transition-all"
                                    data-testid={`ai-briefing-action-${i}`}
                                >
                                    <div className="w-9 h-9 rounded-xl bg-[#FF914D]/10 flex items-center justify-center shrink-0">
                                        <IconComp className="w-4 h-4 text-[#FF914D]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p
                                            className="text-sm font-semibold text-white truncate"
                                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                        >
                                            {action.label}
                                        </p>
                                        <p
                                            className="text-xs text-foreground/50 truncate"
                                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                        >
                                            {action.sub}
                                        </p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-foreground/30 group-hover:text-[#FF914D] group-hover:translate-x-0.5 transition-all shrink-0 mt-2" />
                                </motion.button>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-foreground/10 flex items-center justify-between text-[11px] text-foreground/40">
                    <div className="flex items-center gap-1.5">
                        <Activity className="w-3 h-3 text-[#FF914D]" />
                        <span style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            Analyse en temps réel · Homly AI
                        </span>
                    </div>
                    <Link
                        to="/admin"
                        onClick={() => onAction(null)}
                        className="text-[#FF914D] font-semibold hover:text-[#FF914D]/80 inline-flex items-center gap-1"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                        Synthèse complète
                        <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </motion.aside>
        </>,
        document.body
    );
}
